<script setup lang="ts">
import { computed } from 'vue'
import { Check, X } from '@lucide/vue'
import BrandMark from '@/components/app/BrandMark.vue'

/**
 * Toast de l'application (SP-37), rendu par vue-sonner comme composant : la pousse de la
 * marque, une pastille d'état (sauge = réussi, destructive = erreur), un titre court en
 * serif et, pour une erreur, le message de l'API s'il apporte quelque chose. Même dessin
 * que `Toast` côté mobile. Jamais de donnée de santé ici : des libellés fixes et le
 * message de l'API seulement.
 */
const props = defineProps<{ variant: 'success' | 'error'; title: string; detail?: string }>()
defineEmits<{ closeToast: [] }>()

const isSuccess = computed(() => props.variant === 'success')
const bandStyle = computed(() => ({
  background: `linear-gradient(155deg, color-mix(in srgb, ${isSuccess.value ? 'var(--sage)' : 'var(--destructive)'} 12%, transparent), transparent 70%), var(--popover)`,
}))
</script>

<template>
  <div
    class="relative flex w-[356px] max-w-[calc(100vw-2rem)] items-center gap-3 rounded-xl border border-border py-3.5 pr-10 pl-4 shadow-glass backdrop-blur-xl"
    :style="bandStyle"
    role="status"
    aria-live="polite"
  >
    <span class="relative shrink-0" aria-hidden="true">
      <BrandMark :wordmark="false" :size="38" />
      <span
        class="absolute -right-1.5 -bottom-1.5 grid size-5 place-items-center rounded-full border-2 border-popover"
        :class="isSuccess ? 'bg-sage text-white' : 'bg-destructive text-destructive-foreground'"
      >
        <Check v-if="isSuccess" class="size-3" :stroke-width="3" />
        <X v-else class="size-3" :stroke-width="3" />
      </span>
    </span>
    <span class="min-w-0 flex-1">
      <span class="block font-serif text-[17px] leading-tight font-medium text-foreground">{{ title }}</span>
      <span v-if="detail" class="mt-0.5 block text-xs leading-snug text-muted-foreground">{{ detail }}</span>
    </span>
    <button
      type="button"
      class="absolute top-2 right-2 grid size-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
      aria-label="Fermer"
      @click="$emit('closeToast')"
    >
      <X class="size-3.5" :stroke-width="1.9" />
    </button>
  </div>
</template>
